import {
  useDialogContext,
  useHeaderContext,
  GlobalHeader,
  WarmingUp,
} from "@WahooFitness/wahoo-offline-mfe";
import usePairedDevice from "@/hooks/usePairedDevice";
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Skeleton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback, useMemo, useState, FocusEvent, useEffect } from "react";
import WSMSensorConnectionState from "@WahooFitness/wsm-native/dist/esm/types/connection_state";
import SignalStrengthIcon from "../SignalStrengthIcon";
import { t, Trans } from "@lingui/macro";
import BatteryLevelIcon from "../BatteryLevelIcon";
import { TrackrRadarConfig } from "./TrackrRadarConfig";
import { useRemoteSensors } from "@/hooks/useRemoteSensors";
import { WheelCircumferenceConfig } from "./WheelCircumferenceConfig";
import { PowerMeterConfig } from "./PowerMeterConfig";
import { BikeGearConfig } from "./BikeGearConfig";
import { TirePressureConfig } from "./TirePressureConfig";
import { BodyPositionConfig } from "./BodyPositionConfig";
import {
  MenuList,
  MenuListItemType,
  MenuListItemVariant,
  WfIconNavigationCloseRegular,
} from "@WahooFitness/redesignr";
import { KCruxSensorProductType } from "@WahooFitness/wsm-native/dist/esm/types/kc/KCruxSensorProductType";
import { ReactRouterLinkWrapper } from "@/components/MenuList/ReactRouterLinkWrapper";
import useDrawerPresentation from "@/hooks/useDrawerPresentation";

interface RemoteSensorDetailProps {
  appToken: string;
  remoteSensorId: number;
}

const RemoteSensorDetail: React.FC<RemoteSensorDetailProps> = ({ appToken, remoteSensorId }) => {
  const { setDialog, handleClose } = useDialogContext();
  const { navigateBack } = useHeaderContext();
  const { isDrawer, close } = useDrawerPresentation("remoteSensorDetail");
  const { device } = usePairedDevice(appToken);
  const { getRemoteSensor, saveDisplayName, forgetRemoteSensor, getDisplayName, getImageSrc } =
    useRemoteSensors(appToken);
  const remoteSensor = useMemo(
    () => getRemoteSensor(remoteSensorId),
    [getRemoteSensor, remoteSensorId]
  );
  const sensorName = useMemo(() => getDisplayName(remoteSensor), [getDisplayName, remoteSensor]);
  const sensorImageSrc = useMemo(() => getImageSrc(remoteSensor), [getImageSrc, remoteSensor]);

  const { palette } = useTheme();

  const [displayNameInitialized, setDisplayNameInitialized] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [displayNameInputValue, setDisplayNameInputValue] = useState("");

  // Add remoteSensor to the dependency array to recalculate when it changes
  const supportsWheelCircumference = useMemo(
    () => remoteSensor?.cfg?.wheel_circ_mm !== undefined,
    [remoteSensor]
  );

  const supportsCrankLength = useMemo(
    () => remoteSensor?.cfg?.crank_length_m !== undefined,
    [remoteSensor]
  );

  const supportsPowerMeterCalibration = useMemo(
    () => remoteSensor?.cfg?.pm_auto_calib !== undefined,
    [remoteSensor]
  );

  const supportsGearConfig = useMemo(
    () => remoteSensor?.cfg?.bike_gear_cfg !== undefined,
    [remoteSensor]
  );

  const supportsBodyPosition = useMemo(
    () =>
      remoteSensor?.status?.sensor_product_type === KCruxSensorProductType.FLOWBIO &&
      remoteSensor?.cfg?.sensor_location !== undefined,
    [remoteSensor]
  );

  useEffect(() => {
    if (!displayNameInitialized && sensorName) {
      setDisplayName(sensorName || "");
      setDisplayNameInputValue(sensorName || "");
      setDisplayNameInitialized(true);
    }
  }, [displayNameInitialized, sensorName]);

  const onDisplayNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value.includes("\n")) {
        return;
      }
      setDisplayName(event.target.value || sensorName || "");
      setDisplayNameInputValue(event.target.value);
      saveDisplayName(remoteSensorId, event.target.value);
    },
    [remoteSensorId, saveDisplayName, sensorName]
  );

  const onDisplayNameFocus = useCallback((event: FocusEvent<HTMLInputElement>) => {
    event.target.select();
  }, []);

  const handleForgetSensor = useCallback(() => {
    setDialog({
      open: true,
      title: t`Unpair ${displayName}`,
      body: t`Are you sure you want to unpair your ${displayName}?`,
      actions: [
        { text: t`Cancel`, action: handleClose },
        {
          text: t`Unpair`,
          color: "error",
          action: async () => {
            const unpairResult = await forgetRemoteSensor(remoteSensorId);
            handleClose();
            if (unpairResult) {
              navigateBack();
            }
          },
        },
      ],
    });
  }, [displayName, forgetRemoteSensor, handleClose, navigateBack, remoteSensorId, setDialog]);

  const sensorInfoListItems = useMemo(
    (): MenuListItemType[] => [
      {
        id: "remote_sensor_ant_id",
        content: t`ID`,
        secondaryContent:
          // Display the ANT ID if available
          remoteSensor?.status?.ant?.[0]?.ant_device_num
            ? remoteSensor.status?.ant[0].ant_device_num.toString()
            : t`N/A`,
        variant: MenuListItemVariant.NoAction,
        hidden: !remoteSensor?.status?.ant?.length,
      },
    ],
    [remoteSensor]
  );

  const sensorInfoFooter = useMemo(() => {
    if (!device || !remoteSensor) {
      return null;
    }
    if (remoteSensor.status.ant?.length === 0) {
      return null;
    }
    return (
      <Box display="flex" flexDirection="column">
        <Typography variant="ui-base-medium" color={palette.text.secondary} mx={2}>
          <Trans>Sensor info</Trans>
        </Typography>
        <MenuList listItems={sensorInfoListItems} disableGutters />
      </Box>
    );
  }, [device, palette.text.secondary, remoteSensor, sensorInfoListItems]);

  // DEX-788: Tymewear VitalPro Breathing sensors expose a deeplink to the ventilation zones
  // screen via the Wahoo marketing link.
  const ventilationZonesFooter = useMemo(() => {
    if (
      !device ||
      !remoteSensor ||
      remoteSensor.status.sensor_product_type !== KCruxSensorProductType.VITALPRO_BREATHING
    ) {
      return null;
    }
    const ventilationZonesListItems: MenuListItemType[] = [
      {
        id: "ventilation_zones",
        content: t`Ventilation zones`,
        variant: MenuListItemVariant.InternalLink,
        linkLocation: "/zones/ventilation-zones",
        linkComponent: ReactRouterLinkWrapper,
      },
    ];
    return <MenuList listItems={ventilationZonesListItems} disableGutters />;
  }, [device, remoteSensor]);

  const additionalSettingsFooter = useMemo(() => {
    if (!device || !remoteSensor) {
      return null;
    }
    switch (remoteSensor.status.sensor_product_type) {
      case KCruxSensorProductType.WAHOO_KICKR_BIKE:
      case KCruxSensorProductType.WAHOO_KICKR_BIKE_SHIFT:
      case KCruxSensorProductType.WAHOO_KICKR:
      case KCruxSensorProductType.WAHOO_KICKR_CORE:
      case KCruxSensorProductType.WAHOO_KICKR_ROLLR:
      case KCruxSensorProductType.WAHOO_KICKR_SNAP:
      case KCruxSensorProductType.WAHOO_KICKR_MOVE:
      case KCruxSensorProductType.WAHOO_POWER_PEDALS_LEFT:
      case KCruxSensorProductType.WAHOO_TICKR_X:
      case KCruxSensorProductType.WAHOO_TRACKR_RADAR:
        return (
          <Alert severity="info" variant="filled">
            <Box display="flex" flexDirection="column" rowGap={1}>
              <Typography variant="prose-base-medium">
                <Trans>More settings available</Trans>
              </Typography>
              <Typography variant="prose-xs">
                <Trans>
                  Pair this sensor directly with the Wahoo app and navigate to the Devices tab for
                  the full list of settings
                </Trans>
              </Typography>
            </Box>
          </Alert>
        );
      default:
        if (supportsGearConfig) {
          return (
            <Alert severity="info" variant="filled">
              <Box display="flex" flexDirection="column" rowGap={1}>
                <Typography variant="prose-base-medium">
                  <Trans>Note</Trans>
                </Typography>
                <Typography variant="prose-xs">
                  <Trans>
                    Changes on this page affect the gear display fields on ELEMNT but do not get
                    shared back to the drivetrain hardware. You should also set up your gears in
                    your drivetrain&apos;s companion app.
                  </Trans>
                </Typography>
              </Box>
            </Alert>
          );
        }
        return null;
    }
  }, [device, remoteSensor, supportsGearConfig]);

  const supportsBattery = useMemo(() => {
    return (
      remoteSensor?.status.connection_state === WSMSensorConnectionState.CONNECTED &&
      remoteSensor?.status.batt_perc !== undefined &&
      remoteSensor?.status.batt_perc !== 255
    );
  }, [remoteSensor?.status.batt_perc, remoteSensor?.status.connection_state]);

  const singleBatteryIsCharging = useMemo(() => {
    return supportsBattery && remoteSensor?.status.is_charging === true;
  }, [remoteSensor?.status.is_charging, supportsBattery]);

  const singleBatteryPercent = useMemo(() => {
    return supportsBattery ? remoteSensor?.status.batt_perc : undefined;
  }, [remoteSensor?.status.batt_perc, supportsBattery]);

  const supportsMultipleBatteries = useMemo(() => {
    const hasSingleBattery = supportsBattery;
    const hasSecondBattery =
      remoteSensor?.status.connection_state === WSMSensorConnectionState.CONNECTED &&
      remoteSensor?.sensors?.some(
        (sensor) =>
          (sensor.status.sensor_product_type === KCruxSensorProductType.WAHOO_POWER_PEDALS_RIGHT &&
            sensor.status.is_paired) ||
          (sensor.status.batt_perc !== undefined && sensor.status.batt_perc !== 255)
      );
    return hasSingleBattery && hasSecondBattery;
  }, [remoteSensor?.sensors, remoteSensor?.status.connection_state, supportsBattery]);

  const secondBatteryIsCharging = useMemo(() => {
    return (
      supportsMultipleBatteries &&
      remoteSensor?.sensors?.find(
        (sensor) => sensor.status.batt_perc !== undefined && sensor.status.is_charging
      )?.status?.is_charging === true
    );
  }, [remoteSensor?.sensors, supportsMultipleBatteries]);

  const secondBatteryPercent = useMemo(() => {
    if (!supportsMultipleBatteries) {
      return undefined;
    }
    return remoteSensor?.sensors?.find((sensor) => sensor.status.batt_perc !== undefined)?.status
      ?.batt_perc;
  }, [remoteSensor?.sensors, supportsMultipleBatteries]);

  const connectionStateAndBatteryItems = useMemo(() => {
    const connectionStatusDisplay = (
      <Box display="flex" flexDirection="row" gap={0.5} alignItems="center">
        {remoteSensor?.status.connection_state === WSMSensorConnectionState.CONNECTED && (
          <SignalStrengthIcon rssi={remoteSensor?.status.rssi} />
        )}
        <Typography variant="ui-lg-medium" color="text.secondary">
          {remoteSensor?.status.connection_state === WSMSensorConnectionState.CONNECTED
            ? t`Connected`
            : t`Disconnected`}
        </Typography>
      </Box>
    );

    const primaryBatteryDisplay = supportsBattery && (
      <Box display="flex" flexDirection="row" gap={0.5} alignItems="center">
        <BatteryLevelIcon
          batteryPercentage={singleBatteryPercent}
          isCharging={singleBatteryIsCharging}
        />
        <Typography variant="ui-lg-medium" color="text.secondary">
          {singleBatteryPercent !== undefined ? t`${singleBatteryPercent}%` : ""}
        </Typography>
      </Box>
    );

    const secondaryBatteryDisplay = supportsMultipleBatteries && (
      <Box display="flex" flexDirection="row" gap={0.5} alignItems="center">
        <BatteryLevelIcon
          batteryPercentage={secondBatteryPercent}
          isCharging={secondBatteryIsCharging}
        />
        <Typography variant="ui-lg-medium" color="text.secondary">
          {secondBatteryPercent !== undefined ? t`${secondBatteryPercent}%` : ""}
        </Typography>
      </Box>
    );

    if (supportsMultipleBatteries) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          <Box display="flex" justifyContent="center">
            {connectionStatusDisplay}
          </Box>
          <Box display="flex" flexDirection="row" justifyContent="center" gap={2}>
            {primaryBatteryDisplay}
            {secondaryBatteryDisplay}
          </Box>
        </Box>
      );
    } else {
      return (
        <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center" gap={2}>
          {connectionStatusDisplay}
          {primaryBatteryDisplay}
        </Box>
      );
    }
  }, [
    remoteSensor?.status.connection_state,
    remoteSensor?.status.rssi,
    secondBatteryIsCharging,
    secondBatteryPercent,
    singleBatteryIsCharging,
    singleBatteryPercent,
    supportsBattery,
    supportsMultipleBatteries,
  ]);

  const configItems = useMemo(() => {
    if (!device || !remoteSensor) {
      return null;
    }

    const items = [];

    switch (remoteSensor.status.sensor_product_type) {
      case KCruxSensorProductType.WAHOO_TRACKR_RADAR:
      case KCruxSensorProductType.GENERIC_BIKE_RADAR:
        items.push(<TrackrRadarConfig appToken={appToken} />);
        break;
      case KCruxSensorProductType.GENERIC_TYRE_PRESSURE:
        items.push(<TirePressureConfig appToken={appToken} remoteSensorId={+remoteSensorId} />);
        break;
      default:
        break;
    }

    // Add WheelCircumferenceConfig if supported
    if (supportsWheelCircumference) {
      items.push(<WheelCircumferenceConfig appToken={appToken} remoteSensorId={+remoteSensorId} />);
    }

    // Add PowerMeterConfig if supported
    if (supportsCrankLength || supportsPowerMeterCalibration) {
      items.push(<PowerMeterConfig appToken={appToken} remoteSensorId={+remoteSensorId} />);
    }

    if (supportsGearConfig) {
      items.push(<BikeGearConfig appToken={appToken} remoteSensorId={+remoteSensorId} />);
    }

    if (supportsBodyPosition) {
      items.push(<BodyPositionConfig appToken={appToken} remoteSensorId={+remoteSensorId} />);
    }

    return items.length ? <>{items}</> : null;
  }, [
    device,
    remoteSensor,
    supportsWheelCircumference,
    supportsCrankLength,
    supportsPowerMeterCalibration,
    supportsGearConfig,
    supportsBodyPosition,
    appToken,
    remoteSensorId,
  ]);

  if (!remoteSensor) {
    return <WarmingUp />;
  }

  return (
    <>
      <GlobalHeader
        primaryLeftActionItem={
          isDrawer ? { icon: <WfIconNavigationCloseRegular />, action: close } : undefined
        }
        backAction={isDrawer ? close : navigateBack}
      />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <Box
          display="flex"
          flexDirection="column"
          overflow="hidden"
          width="100%"
          height="100%"
          px={2}
        >
          <Box maxWidth="sm" display="flex" flexDirection="column" overflow="scroll" py={2} gap={4}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
              {sensorImageSrc ? (
                <Box
                  height={150}
                  width={150}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <img
                    src={sensorImageSrc}
                    alt={sensorName}
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  />
                </Box>
              ) : (
                <Skeleton variant="rectangular" height={100} width={100} />
              )}
              <Typography variant="ui-2xl-medium">{displayName}</Typography>
              {connectionStateAndBatteryItems}
            </Box>
            <Box width="100%">
              <Paper elevation={4}>
                <Box p={2}>
                  <TextField
                    variant="filled"
                    label={t`Name`}
                    value={displayNameInputValue}
                    onChange={onDisplayNameChange}
                    onFocus={onDisplayNameFocus}
                    fullWidth
                    multiline
                    inputProps={{ maxLength: 50 }}
                  />
                </Box>
              </Paper>
            </Box>
            {configItems}
            {ventilationZonesFooter}
            {sensorInfoFooter}
            {additionalSettingsFooter}
            <Button variant="text" color="error" size="large" onClick={handleForgetSensor}>
              <Trans>Forget sensor</Trans>
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default RemoteSensorDetail;
